import { Event } from '@/types/event';
import { API_CONFIG } from '@/config';
import { apiClient } from './apiClient';

export async function fetchEvents(): Promise<Event[]> {
  try {
    console.log('Récupération des événements...');
    return await apiClient.get(API_CONFIG.ENDPOINTS.EVENTS);
  } catch (error) {
    console.error('Erreur lors de la récupération des événements:', error);
    throw error;
  }
}

export async function toggleEventInterest(eventId: number): Promise<void> {
  try {
    // Vérifier que l'ID utilisateur est présent dans le token
    const userId = apiClient.getUserId();
    if (!userId) {
      console.error('Aucun ID utilisateur numérique trouvé - vérification des données utilisateur en session');
      
      // Tentative de récupération des informations utilisateur depuis sessionStorage
      const userData = sessionStorage.getItem('userData');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          if (user && user.userId) {
            console.log(`Utilisation de l'ID utilisateur depuis userData: ${user.userId}`);
            // Continuer avec l'ID récupéré
            await apiClient.post(`${API_CONFIG.ENDPOINTS.EVENTS}/${eventId}/interest`);
            return;
          }
        } catch (e) {
          console.error('Erreur lors du parsing des données utilisateur:', e);
        }
      }
      
      throw new Error('Utilisateur non authentifié ou ID utilisateur manquant dans le token');
    }
    
    console.log(`Toggle intérêt pour l'événement ${eventId} avec userId: ${userId}`);
    await apiClient.post(`${API_CONFIG.ENDPOINTS.EVENTS}/${eventId}/interest`);
  } catch (error) {
    console.error('Erreur lors du toggle d\'intérêt:', error);
    throw error;
  }
}

export async function toggleEventSave(eventId: number): Promise<void> {
  try {
    // Vérifier que l'ID utilisateur est présent dans le token
    const userId = apiClient.getUserId();
    if (!userId) {
      throw new Error('Utilisateur non authentifié ou ID utilisateur manquant dans le token');
    }
    
    console.log(`Toggle sauvegarde pour l'événement ${eventId} avec userId: ${userId}`);
    await apiClient.post(`${API_CONFIG.ENDPOINTS.EVENTS}/${eventId}/save`);
  } catch (error) {
    console.error('Erreur lors du toggle de sauvegarde:', error);
    throw error;
  }
}

export interface ViewStats {
  totalViews: number;
  uniqueViewers: number;
  viewsPerDay: { date: string; count: number }[];
}

export async function recordEventView(eventId: number): Promise<{ viewCount: number }> {
  try {
    return await apiClient.post(`${API_CONFIG.ENDPOINTS.EVENTS}/${eventId}/view`);
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement de la vue d\'événement:', error);
    throw error;
  }
}

export async function getEventViewStats(eventId: number): Promise<ViewStats> {
  try {
    return await apiClient.get(`${API_CONFIG.ENDPOINTS.EVENTS}/${eventId}/views/stats`);
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques de vues de l\'événement:', error);
    throw error;
  }
}

/**
 * Récupère les événements que l'utilisateur a marqués comme intéressants
 * @returns Tableau d'événements auxquels l'utilisateur est intéressé
 */
export async function fetchUserInterestedEvents(): Promise<Event[]> {
  try {
    // Vérifier que l'ID utilisateur est présent dans le token
    const userId = apiClient.getUserId();
    if (!userId) {
      throw new Error('Utilisateur non authentifié ou ID utilisateur manquant dans le token');
    }
    
    console.log(`Récupération des événements intéressants pour l'utilisateur ${userId}`);
    return await apiClient.get(`${API_CONFIG.ENDPOINTS.USERS}/me/interested-events`);
  } catch (error) {
    console.error('Erreur lors de la récupération des événements intéressants:', error);
    // En cas d'erreur, retourner un tableau vide au lieu de rejeter la promesse
    return [];
  }
}

/**
 * Récupère les événements que l'utilisateur a sauvegardés
 * @returns Tableau d'événements sauvegardés par l'utilisateur
 */
export async function fetchUserSavedEvents(): Promise<Event[]> {
  try {
    // Vérifier que l'ID utilisateur est présent dans le token
    const userId = apiClient.getUserId();
    if (!userId) {
      throw new Error('Utilisateur non authentifié ou ID utilisateur manquant dans le token');
    }
    
    console.log(`Récupération des événements sauvegardés pour l'utilisateur ${userId}`);
    return await apiClient.get(`${API_CONFIG.ENDPOINTS.USERS}/me/saved-events`);
  } catch (error) {
    console.error('Erreur lors de la récupération des événements sauvegardés:', error);
    // En cas d'erreur, retourner un tableau vide au lieu de rejeter la promesse
    return [];
  }
}

/**
 * Fetch events near a specific location
 * @param coordinates User coordinates (latitude and longitude)
 * @param maxDistance Maximum distance in kilometers (default: 10km)
 * @returns Array of events within the specified distance
 */
export async function fetchNearbyEvents(
  coordinates: { lat: number; lon: number }, 
  maxDistance: number = 10
): Promise<Event[]> {
  try {
    console.log(`Récupération des événements à proximité de [${coordinates.lat}, ${coordinates.lon}] dans un rayon de ${maxDistance}km`);
    
    // Adapter le format des coordonnées pour le backend NestJS
    // Le backend attend 'lat' et 'lon' comme paramètres de requête
    const params = {
      lat: coordinates.lat,
      lon: coordinates.lon,
      maxDistance,
      upcoming: true
    };
    
    console.log('Envoi des paramètres au backend:', params);
    
    return await apiClient.get(`${API_CONFIG.ENDPOINTS.EVENTS}/nearby`, {
      params
    });
  } catch (error) {
    console.error('Erreur lors de la récupération des événements à proximité:', error);
    throw error;
  }
}
